import { useState } from 'react';
import { useQuery } from 'convex/react';
import { MessageSquarePlus, Bug, Lightbulb, MessageCircle } from 'lucide-react';
import { api } from '../../../../convex/_generated/api';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EmptyState } from '@/components/shared/empty-state';
import { ListLoadingSkeleton } from '@/components/shared/list-loading-skeleton';
import { cn } from '@/lib/utils';

type RoleFilter = 'all' | 'coach' | 'director' | 'parent';
type Category = 'bug' | 'feature' | 'general';

const CATEGORY_CONFIG: Record<Category, { label: string; icon: typeof Bug; className: string }> = {
  bug: { label: 'Bug Report', icon: Bug, className: 'bg-red-100 text-red-700 border-red-200' },
  feature: { label: 'Feature Request', icon: Lightbulb, className: 'bg-blue-100 text-blue-700 border-blue-200' },
  general: { label: 'General', icon: MessageCircle, className: 'bg-gray-100 text-gray-700 border-gray-200' },
};

const ROLE_LABELS: Record<string, string> = {
  coach: 'Coach',
  director: 'Director',
  parent: 'Parent',
};

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function DirectorFeedbackPage() {
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');

  const result = useQuery(api.director.feedback.list, {
    roleFilter: roleFilter === 'all' ? undefined : roleFilter,
  });

  const isLoading = result === undefined;
  const submissions = result?.submissions ?? [];
  const total = result?.pagination.total ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <MessageSquarePlus className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">App Feedback</h1>
            <p className="text-sm text-muted-foreground">
              {isLoading ? 'Loading...' : `${total} submission${total !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>

        {/* Role filter */}
        <Select value={roleFilter} onValueChange={(v) => setRoleFilter(v as RoleFilter)}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="coach">Coach</SelectItem>
            <SelectItem value="director">Director</SelectItem>
            <SelectItem value="parent">Parent</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {isLoading ? (
        <ListLoadingSkeleton />
      ) : submissions.length === 0 ? (
        <EmptyState
          icon={<MessageSquarePlus className="w-10 h-10" />}
          title="No feedback yet"
          description="Feedback submitted by coaches, parents, and directors will appear here."
        />
      ) : (
        <div className="space-y-3">
          {submissions.map((item) => {
            const cat = CATEGORY_CONFIG[item.category as Category] ?? CATEGORY_CONFIG.general;
            const CatIcon = cat.icon;
            return (
              <div
                key={item.id}
                className="bg-card rounded-lg border border-border p-4 space-y-3"
              >
                {/* Top row: badges + timestamp */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={cn('text-xs font-medium border', cat.className)}>
                    <CatIcon className="w-3 h-3 mr-1" />
                    {cat.label}
                  </Badge>
                  <Badge variant="secondary" className="text-xs capitalize">
                    {ROLE_LABELS[item.role] ?? item.role}
                  </Badge>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {formatDate(item.createdAt)}
                  </span>
                </div>

                {/* Message */}
                <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                  {item.message}
                </p>

                {/* Footer: submitter + page */}
                <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                  <span>{item.submitterName ?? item.submitterEmail ?? 'Unknown user'}</span>
                  {item.pageUrl && (
                    <>
                      <span>·</span>
                      <span className="font-mono">{item.pageUrl}</span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
